import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";
import {
  AccountType,
  DefaultCurrencyPickListOptions,
  EntityDetailsDto,
  InvestmentAmmountPickListOption,
  InvestmentExperienceLevel,
} from "./api/generated";

export type UploadedFileType = { contentVersionId: string; fileName: string };

export type WealthSourceOptions =
  | "Income"
  | "Savings"
  | "Pension"
  | "Property Income"
  | "Benefits"
  | "Borrowing"
  | "Family Support"
  | "Investments"
  | "Inheritance"
  | "Savings"
  | "Others";

export type AccountPurposeOptions =
  | "Short Term Trading"
  | "Long Term Trading"
  | "Custody of Assets"
  | "Purchase Only"
  | "Sell Only";

export type ApplicationStepsOptionsType =
  | "loading"
  | "accountType"
  | "aboutYourEntity"
  | "aboutYou"
  | "cryptoJourney"
  // Privacy notice & consent page shown immediately before Onfido
  // verification — UK clients only
  | "onfidoConsent"
  | "onfidoVerify"
  | "applicationSuccess"
  | "nonIndividualTypeSelection"
  // UK-specific onboarding steps
  | "ukWelcomeBack"
  | "ukRiskSummary"
  | "ukInvestorType"
  | "ukRestrictedStatement"
  | "ukHnwStatement"
  | "ukAssessment"
  | "ukAssessmentResult"
  | "ukCoolingOff"
  | "ukFinalConfirmation";

export type reKycFormSteps = "aboutYou" | "aboutYourEntity" | "onfidoVerify";

// UK Onboarding Types
export type UkInvestorType = "RESTRICTED" | "HIGH_NET_WORTH" | null;

export type UkAssessmentStatus = "not_started" | "in_progress" | "passed" | "failed" | "locked_out";

export type UkCoolingOffStatus = "not_started" | "in_progress" | "completed";

export type UkFinalConfirmationStatus = "pending" | "confirmed" | "declined" | null;

export interface UkAssessmentQuestion {
  id: string;
  type: "experience" | "professional" | "knowledge";
  questionText: string;
  options: { id: string; text: string }[];
}

export interface UkAssessmentAnswer {
  questionId: string;
  selectedOptionId: string;
}

// Completed journey checkpoints, hydrated from the backend UK status.
export interface UkCheckpoints {
  accountTypeSelected: boolean;
  classificationDone: boolean;
  assessmentDone: boolean;
  idVerificationDone: boolean;
  applicationFormDone: boolean;
}

export interface UkOnboardingState {
  // Account type checkpoint (pause-and-come-back), persisted server-side
  selectedAccountType: "INDIVIDUAL" | "ENTITY" | null;

  // Journey checkpoints from the backend; null until hydrated
  checkpoints: UkCheckpoints | null;

  // Whether the user has made the continue-vs-start-fresh choice this
  // session (session-only — resets on page load so returning users are
  // asked again)
  resumeChoiceMade: boolean;

  // Whether saved progress expired (10 days after step one) and was reset
  // server-side during hydration — used to tell the user why they are
  // starting again
  progressExpired: boolean;

  // Risk Summary
  riskSummaryViewed: boolean;

  // Investor Type Selection
  investorType: UkInvestorType;

  // Restricted Investor Statement
  restrictedStatement: {
    declarationAgreed: boolean;
  };

  // High Net Worth Statement
  hnwStatement: {
    declarationAgreed: boolean;
  };

  // Appropriateness Assessment
  assessment: {
    status: UkAssessmentStatus;
    questions: UkAssessmentQuestion[];
    answers: UkAssessmentAnswer[];
    attemptNumber: number;
    maxAttempts: number;
    score: number | null;
    totalQuestions: number | null;
    passThreshold: number;
    knockoutFailed: boolean;
    passedWithWarning: boolean;
    // "Warning and manual review" (score 80-90): the application is parked
    // until support reviews it — the result screen shows the review message
    // on every visit and the assessment cannot be retaken.
    manualReview: boolean;
    resultAcknowledged: boolean;
    lockoutEndsAt: number | null;
    nextAttemptAvailableAt: number | null;
  };

  // Cooling-off Period
  coolingOff: {
    status: UkCoolingOffStatus;
    startedAt: number | null;
    endsAt: number | null;
  };

  // Final Confirmation
  finalConfirmation: UkFinalConfirmationStatus;
}

export type AddressDetails = {
  fullAddress: string | null;
  country: string | null;
  zipCode: string | null;
  city: string | null;
  concil: string | null;
  street: string | null;
  streetNumber: string | null;
  unitNumber: string | null;
  state: string | null;
};

export type RegisteredOfficeAddressType = {
  registeredOfficeFullAddress: string | null;
  council: string | null;
  registeredOfficeStreetNumber: string | null;
  registeredOfficeCountry: string;
  registeredOfficeCity: string;
  registeredOfficeState: string;
  registeredOfficePostcode: string;
  registeredOfficeStreet: string;
  registeredOfficeunitApartment: string;
};

export type PlaceOfBusinessAddressType = {
  placeOfBusinessFullAddress: string | null;
  council: string | null;
  placeOfBusinessStreetNumber: string | null;
  placeOfBusinessCountry: string;
  placeOfBusinessCity: string;
  placeOfBusinessState: string;
  placeOfBusinessPostCode: string;
  placeOfBusinessStreet: string;
  placeOfBusinessunitApartment: string;
};

export type BirthdateType = {
  day: string;
  month: string;
  year: string;
};

export interface uploadedFilesType extends File {
  contentVersionId: string;
  isLoading: boolean;
  isDeleting: boolean;
  isError: boolean;
  documentType?: "Entity";
}

export interface UserState {
  accountType: AccountType | null;
  firstName: string;
  lastName: string;
  preferredName: string;
  recordType: EntityDetailsDto["recordType"];
  applicationStep: ApplicationStepsOptionsType;
  reKycApplicationStep: reKycFormSteps;
  address: Partial<AddressDetails>;
  phone: string;
  nationality: string;
  ssn: string;
  doesNotHoldSSN: boolean;
  birthdate: BirthdateType;
  cryptoJourney: {
    wealthSource: WealthSourceOptions[];
    investmentExp: InvestmentExperienceLevel | "";
    defaultCurrency: DefaultCurrencyPickListOptions | "";
    investmentPlan: InvestmentAmmountPickListOption;
    occupation: string;
    industry: string;
    frequency: string;
    hearAboutUs: string;
    otherWealthSource: string;
    referralCode: string;
    portfolioValue: InvestmentAmmountPickListOption;
    accountPurpose: AccountPurposeOptions[];
  };
  entityDetails: EntityDetailsDto;
  uploadedFiles: uploadedFilesType[];
  registeredOfficeAddress: RegisteredOfficeAddressType;
  placeOfBusinessAddress: PlaceOfBusinessAddressType;
  reKycEntityId: string;
  sameAsRegisteredOfficeAddress: boolean;
  ignoreBiometrics: boolean;
  ignoreFileUpload: boolean;
  prefilledInvestmentExp: boolean;
  isMultiAccountComponentRendered: boolean;
  // UK-specific onboarding state
  ukOnboarding: UkOnboardingState;
}

const initialState: UserState = {
  accountType: null,
  firstName: "",
  lastName: "",
  recordType: "Other",
  applicationStep: "loading",
  preferredName: "",
  ssn: "",
  doesNotHoldSSN: false,
  address: {
    city: "",
    concil: "",
    country: "",
    fullAddress: "",
    state: "",
    street: "",
    streetNumber: "",
    unitNumber: "",
    zipCode: "",
  },
  phone: "",
  nationality: "",
  birthdate: {
    day: "",
    month: "",
    year: "",
  },
  cryptoJourney: {
    wealthSource: [],
    investmentExp: "",
    defaultCurrency: "",
    investmentPlan: "$500-$10K",
    occupation: "",
    industry: "",
    frequency: "",
    hearAboutUs: "",
    otherWealthSource: "",
    referralCode: "",
    portfolioValue: "$0-$10K",
    accountPurpose: [],
  },
  entityDetails: {
    address: {
      city: "",
      country: "",
      street: "",
      zipCode: "",
      state: "",
    },
    id: "",
    name: "",
    natureOfBusiness: "",
    otherCompanyType: "",
    recordType: "Other",
    companyType: "",
  },
  uploadedFiles: [],
  reKycApplicationStep: "onfidoVerify",
  registeredOfficeAddress: {
    registeredOfficeFullAddress: "",
    council: "",
    registeredOfficeStreetNumber: "",
    registeredOfficeCountry: "",
    registeredOfficeCity: "",
    registeredOfficeState: "",
    registeredOfficePostcode: "",
    registeredOfficeStreet: "",
    registeredOfficeunitApartment: "",
  },
  placeOfBusinessAddress: {
    placeOfBusinessFullAddress: "",
    council: "",
    placeOfBusinessStreetNumber: "",
    placeOfBusinessCity: "",
    placeOfBusinessCountry: "",
    placeOfBusinessState: "",
    placeOfBusinessPostCode: "",
    placeOfBusinessStreet: "",
    placeOfBusinessunitApartment: "",
  },
  reKycEntityId: "",
  sameAsRegisteredOfficeAddress: false,
  ignoreBiometrics: false,
  ignoreFileUpload: false,
  prefilledInvestmentExp: false,
  isMultiAccountComponentRendered: false,
  // UK Onboarding initial state
  ukOnboarding: {
    selectedAccountType: null,
    checkpoints: null,
    resumeChoiceMade: false,
    progressExpired: false,
    riskSummaryViewed: false,
    investorType: null,
    restrictedStatement: {
      declarationAgreed: false,
    },
    hnwStatement: {
      declarationAgreed: false,
    },
    assessment: {
      status: "not_started",
      questions: [],
      answers: [],
      attemptNumber: 0,
      maxAttempts: 3,
      score: null,
      totalQuestions: null,
      passThreshold: 70,
      knockoutFailed: false,
      passedWithWarning: false,
      manualReview: false,
      resultAcknowledged: false,
      lockoutEndsAt: null,
      nextAttemptAvailableAt: null,
    },
    coolingOff: {
      status: "not_started",
      startedAt: null,
      endsAt: null,
    },
    finalConfirmation: null,
  },
};

const OnboardingSlice = createSlice({
  name: "onboarding",
  initialState: initialState,
  reducers: {
    setAccountSelection: (state, { payload }: PayloadAction<AccountType | null>) => {
      state.accountType = payload;
    },
    setFirstName: (state, { payload }: PayloadAction<string>) => {
      state.firstName = payload;
    },
    setLastName: (state, { payload }: PayloadAction<string>) => {
      state.lastName = payload;
    },
    setReKycApplicationStep: (state, { payload }: PayloadAction<reKycFormSteps>) => {
      state.reKycApplicationStep = payload;
    },
    setRekycEntityId: (state, { payload }: PayloadAction<string>) => {
      state.reKycEntityId = payload;
    },
    setRecordType: (state, { payload }: PayloadAction<EntityDetailsDto["recordType"]>) => {
      state.entityDetails.recordType = payload;
    },
    setOtherCompanyType: (state, { payload }: PayloadAction<string>) => {
      state.entityDetails.otherCompanyType = payload;
    },
    setNatureOfBusiness: (state, { payload }: PayloadAction<string>) => {
      state.entityDetails.natureOfBusiness = payload;
    },
    setApplicationStep: (state, { payload }: PayloadAction<ApplicationStepsOptionsType>) => {
      state.applicationStep = payload;
    },
    setAddress: (state, { payload }: PayloadAction<Partial<AddressDetails>>) => {
      state.address = { ...state.address, ...payload };
    },
    setEntityAddress: (state, { payload }: PayloadAction<Partial<EntityDetailsDto["address"]>>) => {
      state.entityDetails.address = { ...state.entityDetails.address, ...payload };
    },
    setRegisteredOfficeAddress: (
      state,
      { payload }: PayloadAction<Partial<RegisteredOfficeAddressType>>,
    ) => {
      state.registeredOfficeAddress = { ...state.registeredOfficeAddress, ...payload };
    },
    setPlaceOfBusinessAddress: (
      state,
      { payload }: PayloadAction<Partial<PlaceOfBusinessAddressType>>,
    ) => {
      state.placeOfBusinessAddress = { ...state.placeOfBusinessAddress, ...payload };
    },
    resetAddress: (state) => {
      state.address = {};
    },
    resetEntityAddress: (state) => {
      state.entityDetails.address = { city: "", country: "", street: "", zipCode: "", state: "" };
    },
    setReduxPhone: (state, { payload }: PayloadAction<string>) => {
      state.phone = payload;
    },
    setNationality: (state, { payload }: PayloadAction<string>) => {
      state.nationality = payload;
    },
    setBirthdate: (state, { payload }: PayloadAction<BirthdateType>) => {
      state.birthdate = payload;
    },
    setReduxSourceOfFunds: (state, { payload }: PayloadAction<WealthSourceOptions[]>) => {
      state.cryptoJourney.wealthSource = payload;
    },
    setReduxCryptoExperience: (
      state,
      { payload }: PayloadAction<InvestmentExperienceLevel | "">,
    ) => {
      state.cryptoJourney.investmentExp = payload;
    },
    setReduxQuoteCurrency: (
      state,
      { payload }: PayloadAction<DefaultCurrencyPickListOptions | "">,
    ) => {
      state.cryptoJourney.defaultCurrency = payload;
    },
    setInvestmentPlan: (state, { payload }: PayloadAction<InvestmentAmmountPickListOption>) => {
      state.cryptoJourney.investmentPlan = payload;
    },
    setPortfolioValue: (state, { payload }: PayloadAction<InvestmentAmmountPickListOption>) => {
      state.cryptoJourney.portfolioValue = payload;
    },
    setOccupation: (state, { payload }: PayloadAction<string>) => {
      state.cryptoJourney.occupation = payload;
    },
    setIndustry: (state, { payload }: PayloadAction<string>) => {
      state.cryptoJourney.industry = payload;
    },
    setFrequency: (state, { payload }: PayloadAction<string>) => {
      state.cryptoJourney.frequency = payload;
    },
    setAccountPurpose: (state, { payload }: PayloadAction<AccountPurposeOptions[]>) => {
      state.cryptoJourney.accountPurpose = payload;
    },
    setHearAboutUs: (state, { payload }: PayloadAction<string>) => {
      state.cryptoJourney.hearAboutUs = payload;
    },
    setOtherWealthSource: (state, { payload }: PayloadAction<string>) => {
      state.cryptoJourney.otherWealthSource = payload;
    },
    setReferralCode: (state, { payload }: PayloadAction<string>) => {
      state.cryptoJourney.referralCode = payload;
    },
    setSSN: (state, { payload }: PayloadAction<string>) => {
      state.ssn = payload;
    },
    setUploadedFiles: (state, { payload }: PayloadAction<uploadedFilesType>) => {
      // Find index of the existing file
      let existingFileIndex = state.uploadedFiles.findIndex((file) => {
        // Update the state of the file that has the same name and size
        return file.name === payload.name && file.size === payload.size;
      });

      // If file exists, update it
      if (existingFileIndex !== -1) {
        state.uploadedFiles[existingFileIndex] = payload;
      }

      // If file doesn't exist, push it
      else {
        state.uploadedFiles.push(payload);
      }
    },
    removeUploadedFile: (state, { payload }: PayloadAction<{ name: string }>) => {
      const newUploadedFiles = state.uploadedFiles.filter((file) => file.name !== payload.name);
      state.uploadedFiles = newUploadedFiles;
    },
    resetFiles: (state) => {
      state.uploadedFiles = [];
    },
    setEntityName: (state, { payload }: PayloadAction<string>) => {
      state.entityDetails.name = payload;
    },
    setEntityId: (state, { payload }: PayloadAction<string>) => {
      state.entityDetails.id = payload;
    },
    setEntityType: (state, { payload }: PayloadAction<EntityDetailsDto["recordType"]>) => {
      state.entityDetails.recordType = payload;
    },
    setCompanyType: (state, { payload }: PayloadAction<EntityDetailsDto["companyType"]>) => {
      state.entityDetails.companyType = payload;
    },
    setSameAsRegisteredOfficeAddress: (state, { payload }: PayloadAction<boolean>) => {
      state.sameAsRegisteredOfficeAddress = payload;
    },
    setUserDoesNotHoldSSN: (state, { payload }: PayloadAction<boolean>) => {
      state.doesNotHoldSSN = payload;
    },
    setPreferredName: (state, { payload }: PayloadAction<string>) => {
      state.preferredName = payload;
    },
    setFormApplicationData: (state, { payload }: PayloadAction<UserState>) => {
      return { ...state, ...payload };
    },
    setIgnoreBiometrics: (state, { payload }: PayloadAction<boolean>) => {
      state.ignoreBiometrics = payload;
    },
    setIgnoreFileUpload: (state, { payload }: PayloadAction<boolean>) => {
      state.ignoreFileUpload = payload;
    },
    setInvestimentExpPrefilled: (state, { payload }: PayloadAction<boolean>) => {
      state.prefilledInvestmentExp = payload;
    },
    setIsMultiAccountComponentRendered: (state, { payload }: PayloadAction<boolean>) => {
      state.isMultiAccountComponentRendered = payload;
    },
    // UK Onboarding Reducers
    setUkSelectedAccountType: (
      state,
      { payload }: PayloadAction<"INDIVIDUAL" | "ENTITY" | null>,
    ) => {
      state.ukOnboarding.selectedAccountType = payload;
    },
    setUkCheckpoints: (state, { payload }: PayloadAction<UkCheckpoints>) => {
      state.ukOnboarding.checkpoints = payload;
    },
    setUkResumeChoiceMade: (state, { payload }: PayloadAction<boolean>) => {
      state.ukOnboarding.resumeChoiceMade = payload;
    },
    setUkProgressExpired: (state, { payload }: PayloadAction<boolean>) => {
      state.ukOnboarding.progressExpired = payload;
    },
    // "Start fresh": clears the re-doable journey progress locally, mirroring
    // the backend restart endpoint. Assessment questions/answers and the last
    // result are cleared so a stale result screen can't show after a restart,
    // but attempts, lockout timers and cooling-off are deliberately kept —
    // the backend preserves and re-enforces them.
    resetUkProgressForRestart: (state) => {
      state.ukOnboarding.selectedAccountType = null;
      state.ukOnboarding.checkpoints = null;
      state.ukOnboarding.riskSummaryViewed = false;
      state.ukOnboarding.investorType = null;
      state.ukOnboarding.restrictedStatement = { declarationAgreed: false };
      state.ukOnboarding.hnwStatement = { declarationAgreed: false };
      state.accountType = null;
      state.ukOnboarding.assessment = {
        ...state.ukOnboarding.assessment,
        questions: [],
        answers: [],
        score: null,
        knockoutFailed: false,
        passedWithWarning: false,
        resultAcknowledged: false,
        status: "not_started",
      };
    },
    setUkRiskSummaryViewed: (state, { payload }: PayloadAction<boolean>) => {
      state.ukOnboarding.riskSummaryViewed = payload;
    },
    setUkInvestorType: (state, { payload }: PayloadAction<UkInvestorType>) => {
      state.ukOnboarding.investorType = payload;
    },
    setUkRestrictedStatement: (
      state,
      {
        payload,
      }: PayloadAction<{
        declarationAgreed?: boolean;
      }>,
    ) => {
      state.ukOnboarding.restrictedStatement = {
        ...state.ukOnboarding.restrictedStatement,
        ...payload,
      };
    },
    setUkHnwStatement: (
      state,
      {
        payload,
      }: PayloadAction<{
        declarationAgreed?: boolean;
      }>,
    ) => {
      state.ukOnboarding.hnwStatement = {
        ...state.ukOnboarding.hnwStatement,
        ...payload,
      };
    },
    setUkAssessmentQuestions: (state, { payload }: PayloadAction<UkAssessmentQuestion[]>) => {
      state.ukOnboarding.assessment.questions = payload;
      state.ukOnboarding.assessment.status = "in_progress";
    },
    setUkAssessmentAnswer: (state, { payload }: PayloadAction<UkAssessmentAnswer>) => {
      const existingIndex = state.ukOnboarding.assessment.answers.findIndex(
        (a) => a.questionId === payload.questionId,
      );
      if (existingIndex !== -1) {
        state.ukOnboarding.assessment.answers[existingIndex] = payload;
      } else {
        state.ukOnboarding.assessment.answers.push(payload);
      }
    },
    setUkAssessmentResult: (
      state,
      {
        payload,
      }: PayloadAction<{
        status: UkAssessmentStatus;
        score?: number | null;
        totalQuestions?: number | null;
        knockoutFailed?: boolean;
        passedWithWarning?: boolean;
        manualReview?: boolean;
        attemptNumber?: number;
        lockoutEndsAt?: number | null;
        nextAttemptAvailableAt?: number | null;
      }>,
    ) => {
      state.ukOnboarding.assessment = {
        ...state.ukOnboarding.assessment,
        ...payload,
      };
    },
    setUkAssessmentResultAcknowledged: (state, { payload }: PayloadAction<boolean>) => {
      state.ukOnboarding.assessment.resultAcknowledged = payload;
    },
    resetUkAssessment: (state) => {
      state.ukOnboarding.assessment = {
        ...state.ukOnboarding.assessment,
        questions: [],
        answers: [],
        score: null,
        knockoutFailed: false,
        passedWithWarning: false,
        resultAcknowledged: false,
        status: "not_started",
      };
    },
    setUkCoolingOff: (
      state,
      {
        payload,
      }: PayloadAction<{
        status?: UkCoolingOffStatus;
        startedAt?: number | null;
        endsAt?: number | null;
      }>,
    ) => {
      state.ukOnboarding.coolingOff = {
        ...state.ukOnboarding.coolingOff,
        ...payload,
      };
    },
    setUkFinalConfirmation: (state, { payload }: PayloadAction<UkFinalConfirmationStatus>) => {
      state.ukOnboarding.finalConfirmation = payload;
    },
    resetUkOnboarding: (state) => {
      state.ukOnboarding = initialState.ukOnboarding;
    },
  },
});

export const {
  setAccountSelection,
  setFirstName,
  setLastName,
  setApplicationStep,
  setAddress,
  setEntityAddress,
  setReduxPhone,
  setBirthdate,
  setNationality,
  setReduxSourceOfFunds,
  setReduxCryptoExperience,
  setReduxQuoteCurrency,
  setInvestmentPlan,
  setPortfolioValue,
  setOccupation,
  setIndustry,
  setFrequency,
  setHearAboutUs,
  setAccountPurpose,
  setOtherWealthSource,
  setReferralCode,
  setUploadedFiles,
  removeUploadedFile,
  setRecordType,
  setEntityName,
  setEntityId,
  setEntityType,
  setOtherCompanyType,
  setNatureOfBusiness,
  resetFiles,
  resetAddress,
  resetEntityAddress,
  setSSN,
  setUserDoesNotHoldSSN,
  setCompanyType,
  setReKycApplicationStep,
  setRegisteredOfficeAddress,
  setPlaceOfBusinessAddress,
  setRekycEntityId,
  setSameAsRegisteredOfficeAddress,
  setPreferredName,
  setFormApplicationData,
  setIgnoreBiometrics,
  setIgnoreFileUpload,
  setInvestimentExpPrefilled,
  setIsMultiAccountComponentRendered,
  // UK Onboarding actions
  setUkSelectedAccountType,
  setUkCheckpoints,
  setUkResumeChoiceMade,
  setUkProgressExpired,
  resetUkProgressForRestart,
  setUkRiskSummaryViewed,
  setUkInvestorType,
  setUkRestrictedStatement,
  setUkHnwStatement,
  setUkAssessmentQuestions,
  setUkAssessmentAnswer,
  setUkAssessmentResult,
  setUkAssessmentResultAcknowledged,
  resetUkAssessment,
  setUkCoolingOff,
  setUkFinalConfirmation,
  resetUkOnboarding,
} = OnboardingSlice.actions;

export const useOnboarding = () => useAppSelector((state) => state.onboarding);

export default OnboardingSlice;
